import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import Loader from "components/Loader";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import {
  CLIENT_DOCUMENT_EVENTS_QUERY_KEY,
  CLIENT_DOCUMENT_REQUESTS_QUERY_KEY,
  deleteDocument,
  downloadDocument,
  fetchDocumentLibrary,
  fetchRequestEvents,
  linkDocumentToModule,
  requestReupload,
  resendDocumentRequest,
  revokeDocumentRequest,
  unlinkDocumentFromModule,
  uploadDocumentAsStaff,
} from "./clientDocuments.api";
import {
  canRequestReupload,
  deriveRequestStatus,
  liveAndHistory,
  saveBlob,
} from "./clientDocuments.utils";
import FlagForReuploadDialog from "./FlagForReuploadDialog";
import ShowInModuleDialog from "./ShowInModuleDialog";
import DocumentFileRow from "./DocumentFileRow";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import DocumentRequestTimeline from "./DocumentRequestTimeline";

const LIBRARY_QUERY_KEY = "client-document-library";

const ItemPanel = ({
  item,
  fileActions,
  busy,
  canDelete,
  canLink,
  canUpload,
  onUploadForStaff,
  requestStatus,
}) => {
  const { live, history } = liveAndHistory(item);
  const fileInputRef = useRef(null);

  const handleFileChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      onUploadForStaff(item, file);
    }
  };

  return (
    <Box className="border border-app-border rounded-lg p-3 space-y-2">
      <Box className="flex items-center justify-between gap-2">
        <Box>
          <Typography variant="subtitle2">
            {item.label}
            {item.required ? "" : " (optional)"}
          </Typography>
          {item.instructions && (
            <Typography variant="caption" className="text-app-text-muted" display="block">
              {item.instructions}
            </Typography>
          )}
          {item.state === "FLAGGED" && (
            <Typography variant="caption" color="info.main" display="block">
              Reupload requested{item.flagReason ? ` — ${item.flagReason}` : ""}. The files below
              stay until a replacement arrives.
            </Typography>
          )}
          {item.sourceItemId && item.state !== "FLAGGED" && item.flagReason && (
            <Typography variant="caption" className="text-app-text-muted" display="block">
              Reason for reupload: {item.flagReason}
            </Typography>
          )}
        </Box>
        <Box className="flex items-center gap-2 shrink-0">
          {canUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleFileChosen}
                aria-label={`Upload a file for ${item.label}`}
              />
              <Button
                size="small"
                variant="text"
                startIcon={<UploadFileIcon fontSize="small" />}
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              >
                Add file
              </Button>
            </>
          )}
          <Chip
            size="small"
            label={
              // eslint-disable-next-line no-nested-ternary
              item.state === "FULFILLED"
                ? "Collected"
                : item.state === "FLAGGED"
                ? "Reupload requested"
                : "Awaiting file"
            }
            color={
              item.state === "FULFILLED" ? "success" : item.state === "FLAGGED" ? "info" : "warning"
            }
            title={item.flagReason || undefined}
          />
        </Box>
      </Box>

      {live.length === 0 ? (
        <Typography variant="body2" className="text-app-text-muted">
          Nothing uploaded yet.
        </Typography>
      ) : (
        live.map((document) => (
          <DocumentFileRow
            key={document.id}
            document={document}
            busy={busy}
            canDelete={canDelete}
            canLink={canLink}
            moduleLinks={document.moduleLinks ?? []}
            requestStatus={requestStatus}
            deletable
            {...fileActions}
          />
        ))
      )}

      {history.length > 0 && (
        <Accordion disableGutters elevation={0} className="border-0">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0 min-h-0">
            <Typography variant="caption" className="text-app-text-muted">
              History ({history.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails className="px-0 pt-0">
            {history.map((document) => (
              <DocumentFileRow
                key={document.id}
                document={document}
                busy={busy}
                canDelete={canDelete}
                moduleLinks={document.moduleLinks ?? []}
                requestStatus={requestStatus}
                deletable={false}
                {...fileActions}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

const DocumentRequestDetail = ({ clientId, request, onClose }) => {
  const queryClient = useQueryClient();
  const canRevoke = hasPermission(P.CLIENT_DOCUMENT_REQUEST.REVOKE);
  const canResend = hasPermission(P.CLIENT_DOCUMENT_REQUEST.CREATE);
  const canDelete = hasPermission(P.CLIENT_DOCUMENT.DELETE);
  const canLink = hasPermission(P.CLIENT_DOCUMENT.UPDATE);
  const canUpload = hasPermission(P.CLIENT_DOCUMENT.CREATE);

  const [confirm, setConfirm] = useState(null);
  const [busyDocumentId, setBusyDocumentId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reuploadOpen, setReuploadOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState("CLIENT");

  // The client's referral attribution decides whether a partner routing can be offered,
  // for both the reupload dialog and the resend choice.
  const { data: client } = useQuery({
    queryKey: ["client-for-document-request", clientId],
    queryFn: () => axios.get(`${API_URL}/clients/${clientId}`).then((r) => r.data?.data ?? null),
    enabled: Boolean(clientId),
  });
  const canOfferPartnerResend =
    request.assigneeType === "PARTNER" || Boolean(client?.referralPartnerId);
  const isReuploadRound = Boolean(request.parentRequestId);

  // refetchOnMount must stay "always": the QueryClient default is refetchOnMount:false,
  // so reopening this panel would otherwise show the state as it was when it last closed.
  const libraryQuery = useQuery({
    queryKey: [LIBRARY_QUERY_KEY, clientId],
    queryFn: () => fetchDocumentLibrary(clientId),
    enabled: Boolean(clientId),
    refetchOnMount: "always",
  });

  const eventsQuery = useQuery({
    queryKey: [CLIENT_DOCUMENT_EVENTS_QUERY_KEY, clientId, request.id],
    queryFn: () => fetchRequestEvents(clientId, request.id),
    enabled: Boolean(clientId && request.id),
    refetchOnMount: "always",
  });

  const detail = (libraryQuery.data || []).find((entry) => entry.id === request.id);
  const { label, color } = deriveRequestStatus(detail || request);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENT_REQUESTS_QUERY_KEY, clientId] });
    queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY, clientId] });
    queryClient.invalidateQueries({
      queryKey: [CLIENT_DOCUMENT_EVENTS_QUERY_KEY, clientId, request.id],
    });
  };

  const actionMutation = useMutation({
    mutationFn: ({ run }) => run(),
    onSuccess: (result, variables) => {
      toast.success(variables.successMessage || result || "Done");
      setConfirm(null);
      refreshAll();
    },
    onError: (err) => {
      toast.error(err.message);
      setConfirm(null);
    },
  });

  const reuploadMutation = useMutation({
    mutationFn: ({ items, routing }) => requestReupload(clientId, request.id, items, routing),
    onSuccess: (data, variables) => {
      const toPartner = variables?.routing?.assignee === "PARTNER";
      toast.success(
        toPartner
          ? "Reupload requested from the referral partner."
          : "Reupload requested. The client has a new upload link with the reupload message."
      );
      setReuploadOpen(false);
      refreshAll();
    },
    onError: (err) => toast.error(err.message),
  });

  const resendMutation = useMutation({
    mutationFn: (target) => resendDocumentRequest(clientId, request.id, target),
    onSuccess: (message) => {
      toast.success(message || "Sent");
      setResendOpen(false);
      refreshAll();
    },
    onError: (err) => toast.error(err.message),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ item, file }) => uploadDocumentAsStaff(clientId, request.id, item.id, file),
    onSuccess: () => {
      toast.success("File added and marked collected.");
      refreshAll();
    },
    onError: (err) => toast.error(err.message),
  });

  /**
   * Saves the whole tick state in one go: link what is newly ticked, unlink what was unticked.
   * Doing it as a diff keeps the dialog a single Save rather than a link/unlink per checkbox.
   */
  const linkMutation = useMutation({
    mutationFn: async (chosen) => {
      const document = linkTarget;
      const existing = document?.moduleLinks ?? [];
      const chosenKeys = new Set(chosen.map((entry) => `${entry.moduleType}:${entry.entryId}`));
      const removals = existing.filter(
        (link) => !chosenKeys.has(`${link.moduleType}:${link.moduleEntryId}`)
      );
      const existingKeys = new Set(
        existing.map((link) => `${link.moduleType}:${link.moduleEntryId}`)
      );
      const additions = chosen.filter(
        (entry) => !existingKeys.has(`${entry.moduleType}:${entry.entryId}`)
      );
      for (const link of removals) {
        await unlinkDocumentFromModule(clientId, document.id, link.id);
      }
      for (const entry of additions) {
        await linkDocumentToModule(clientId, document.id, entry.moduleType, entry.entryId);
      }
    },
    onSuccess: () => {
      toast.success("Updated where this document is shown.");
      setLinkTarget(null);
      refreshAll();
    },
    onError: (err) => toast.error(err.message),
  });

  const askRevoke = () =>
    setConfirm({
      title: "Revoke this request?",
      message:
        "The client's upload link stops working immediately. Files they have already uploaded are kept. This cannot be undone.",
      confirmLabel: "Revoke",
      confirmColor: "error",
      successMessage: "Request revoked. The upload link no longer works.",
      run: () => revokeDocumentRequest(clientId, request.id),
    });

  const askResend = () => {
    if (!canOfferPartnerResend) {
      setConfirm({
        title: "Resend the invite email?",
        message: isReuploadRound
          ? `This emails the reupload link to the client again for "${request.title}" with the reupload message. The link itself does not change.`
          : `This emails the upload link to the client again for "${request.title}". The link itself does not change.`,
        confirmLabel: "Send email",
        confirmColor: "primary",
        run: () => resendDocumentRequest(clientId, request.id),
      });
      return;
    }
    // Partner-routed rounds (including reuploads) offer the same Send-to choice as a
    // first-time request: notify the referrer in the portal or email the client directly.
    setResendTarget(request.assigneeType === "PARTNER" ? "PARTNER" : "CLIENT");
    setResendOpen(true);
  };

  const askDelete = (document) =>
    setConfirm({
      title: "Delete this file?",
      message: `"${document.originalFilename}" will be removed from this request and moved into History. The item goes back to awaiting a file if this is its last one.`,
      confirmLabel: "Delete",
      confirmColor: "error",
      successMessage: "File removed",
      run: () => deleteDocument(clientId, document.id),
    });

  const handleDownload = async (document) => {
    setBusyDocumentId(document.id);
    try {
      const { blob, filename } = await downloadDocument(
        clientId,
        document.id,
        document.originalFilename
      );
      saveBlob(blob, filename);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyDocumentId(null);
    }
  };

  const handlePreview = async (document) => {
    setPreview({ document, loading: true, blob: null, error: "" });
    try {
      const { blob } = await downloadDocument(clientId, document.id, document.originalFilename);
      setPreview({ document, loading: false, blob, error: "" });
    } catch (err) {
      setPreview({ document, loading: false, blob: null, error: err.message });
    }
  };

  const fileActions = {
    onPreview: handlePreview,
    onDownload: handleDownload,
    onDelete: askDelete,
    onShowInModule: (document) => setLinkTarget(document),
  };

  // portalUrl is null unless the request is still ACTIVE, so a dead request never offers a
  // link that would only 404 for the client.
  const handleCopyLink = async () => {
    if (!request.portalUrl) return;
    try {
      await navigator.clipboard.writeText(request.portalUrl);
      toast.success("Upload link copied");
    } catch {
      toast.error("Could not copy. The link is shown in the request details.");
    }
  };

  return (
    <Box className="border border-app-border rounded-lg p-4 space-y-4">
      <Box className="flex items-start justify-between gap-3">
        <Box>
          <Typography variant="h6">{request.title || "Document request"}</Typography>
          <Box className="flex items-center gap-2 mt-1">
            <Chip size="small" label={label} color={color} />
            {request.parentRequestId && (
              <Chip size="small" variant="outlined" label={request.roundLabel} />
            )}
            {request.origin === "PARTNER" && (
              <Chip
                size="small"
                color="info"
                label={`Referrer requested${
                  request.assigneeName ? ` · ${request.assigneeName}` : ""
                }`}
              />
            )}
          </Box>
        </Box>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
      </Box>

      <Box className="flex flex-wrap gap-2">
        {request.portalUrl && (
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyLink}>
            Copy upload link
          </Button>
        )}
        {canResend && request.status === "ACTIVE" && (
          <Button size="small" startIcon={<SendIcon />} onClick={askResend}>
            {canOfferPartnerResend ? "Resend / notify" : "Resend invite email"}
          </Button>
        )}
        {canRevoke && request.status === "ACTIVE" && (
          <Button size="small" color="error" onClick={askRevoke}>
            Revoke
          </Button>
        )}
        {canResend && canRequestReupload(detail || request) && (
          <Button size="small" startIcon={<ReplayIcon />} onClick={() => setReuploadOpen(true)}>
            Request reupload
          </Button>
        )}
      </Box>

      <Divider />

      {libraryQuery.isLoading && <Loader />}
      {!libraryQuery.isLoading && libraryQuery.error && (
        <Typography variant="body2" color="error">
          {libraryQuery.error.message}
        </Typography>
      )}
      {!libraryQuery.isLoading && !libraryQuery.error && (
        <Box className="space-y-3">
          {(detail?.items || []).map((item) => (
            <ItemPanel
              key={item.id}
              item={item}
              fileActions={fileActions}
              busy={busyDocumentId !== null || actionMutation.isLoading || uploadMutation.isLoading}
              canDelete={canDelete}
              canLink={canLink}
              canUpload={canUpload}
              onUploadForStaff={(uploadItem, file) =>
                uploadMutation.mutate({ item: uploadItem, file })
              }
              requestStatus={(detail || request).status}
            />
          ))}
          {detail && detail.items?.length === 0 && (
            <Typography variant="body2" className="text-app-text-muted">
              This request has no items.
            </Typography>
          )}
        </Box>
      )}

      <Divider />

      <Accordion disableGutters elevation={0} className="border-0">
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
          <Typography variant="subtitle2">
            Activity{eventsQuery.data?.length ? ` (${eventsQuery.data.length})` : ""}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="px-0 pt-0">
          <DocumentRequestTimeline
            events={eventsQuery.data}
            isLoading={eventsQuery.isLoading}
            error={eventsQuery.error}
          />
        </AccordionDetails>
      </Accordion>

      <ShowInModuleDialog
        open={Boolean(linkTarget)}
        clientId={clientId}
        document={linkTarget}
        existingLinks={linkTarget?.moduleLinks ?? []}
        loading={linkMutation.isLoading}
        onCancel={() => setLinkTarget(null)}
        onSave={(chosen) => linkMutation.mutate(chosen)}
      />

      <FlagForReuploadDialog
        open={reuploadOpen}
        request={detail || request}
        client={client}
        loading={reuploadMutation.isLoading}
        onCancel={() => setReuploadOpen(false)}
        onConfirm={(items, routing) => reuploadMutation.mutate({ items, routing })}
      />

      <Dialog open={resendOpen} onClose={() => setResendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resend this request?</DialogTitle>
        <DialogContent>
          <Box className="flex flex-col gap-3 pt-1">
            <Typography variant="body2" className="text-app-text-muted">
              {isReuploadRound
                ? `This round uses the reupload message. The link itself does not change.`
                : `The link itself does not change.`}
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">Send to</FormLabel>
              <RadioGroup value={resendTarget} onChange={(e) => setResendTarget(e.target.value)}>
                <Box className="flex flex-col gap-0.5 py-1">
                  <FormControlLabel
                    value="CLIENT"
                    control={<Radio size="small" />}
                    label="The client (email)"
                  />
                  <Typography variant="caption" className="text-app-text-muted pl-8">
                    {isReuploadRound
                      ? `The client gets the upload link again with the reupload message and the reason.`
                      : `The client gets the upload link again by email.`}
                  </Typography>
                </Box>
                <Box className="flex flex-col gap-0.5 py-1">
                  <FormControlLabel
                    value="PARTNER"
                    control={<Radio size="small" />}
                    label={
                      client?.referralPartnerName
                        ? `${client.referralPartnerName} (referral partner)`
                        : "Referral partner"
                    }
                  />
                  <Typography variant="caption" className="text-app-text-muted pl-8">
                    The partner is notified in the referral portal and collects or uploads like a
                    new request. The client gets no email unless the partner forwards the link.
                  </Typography>
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendOpen(false)} disabled={resendMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={resendMutation.isLoading}
            onClick={() => resendMutation.mutate(resendTarget === "PARTNER" ? "PARTNER" : "CLIENT")}
          >
            {resendMutation.isLoading ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        loading={actionMutation.isLoading}
        onConfirm={() => actionMutation.mutate(confirm)}
        onCancel={() => setConfirm(null)}
      />

      <DocumentPreviewDialog
        open={Boolean(preview)}
        document={preview?.document}
        loading={preview?.loading}
        error={preview?.error}
        blob={preview?.blob}
        onClose={() => setPreview(null)}
        onDownload={handleDownload}
      />
    </Box>
  );
};

export default DocumentRequestDetail;
